import React, { useState, useMemo, useEffect } from 'react';
import Spinner from '../../../Spinner';
import centroid from '@turf/centroid';
import nearbyInfraIcon from '../../../../assets/icons/nearby-infrastructure-icon.png';

export default function NearbyInfrastructure({ nearbyInfra, latitude, longitude, map, calculateInfraDistance, showInfraPopup }) {
    const [selectedType, setSelectedType] = useState('all');
    const [isLayerVisible, setIsLayerVisible] = useState(true);

    // Calculate type breakdown
    const typeBreakdown = useMemo(() => {
        if (!nearbyInfra) return {};
        const breakdown = {};
        nearbyInfra.forEach(infra => {
            const type = infra.properties.type;
            breakdown[type] = (breakdown[type] || 0) + 1;
        });
        return breakdown;
    }, [nearbyInfra]);

    // Get unique types for filter dropdown
    const availableTypes = useMemo(() => {
        return Object.keys(typeBreakdown).sort();
    }, [typeBreakdown]);

    // Filter infrastructure based on selected type
    const filteredInfra = useMemo(() => {
        if (!nearbyInfra) return [];
        if (selectedType === 'all') return nearbyInfra;
        return nearbyInfra.filter(infra => infra.properties.type === selectedType);
    }, [nearbyInfra, selectedType]);

    // Sync checkbox state with actual layer visibility on mount
    useEffect(() => {
        if (map && map.current) {
            try {
                const layer = map.current.getLayer('Layer-nearby-infrastructure');
                if (layer) {
                    const currentVisibility = map.current.getLayoutProperty('Layer-nearby-infrastructure', 'visibility');
                    setIsLayerVisible(currentVisibility === 'visible');
                }
            } catch (error) {
                console.warn('Error checking layer visibility:', error);
            }
        }
    }, [map]);

    // Toggle layer visibility
    const toggleLayerVisibility = () => {
        const newVisibility = !isLayerVisible;
        setIsLayerVisible(newVisibility);
        
        if (map && map.current) {
            try {
                const layer = map.current.getLayer('Layer-nearby-infrastructure');
                if (layer) {
                    map.current.setLayoutProperty('Layer-nearby-infrastructure', 'visibility', newVisibility ? 'visible' : 'none');
                }
            } catch (error) {
                console.warn('Error toggling layer visibility:', error);
            }
        }
    };
    return (
        !nearbyInfra ?
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                <div style={{ fontSize: '0.8rem', color: 'black' }}>Searching for nearby infrastructure potentially at risk...</div>
                <Spinner />
            </div>
            :     
            <div style={{ width: '100%', marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ color: 'black', fontSize: '0.9rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <img style={{ width: '1.2rem' }} src={nearbyInfraIcon}/>
                    Infrastructure at risk ({nearbyInfra ? nearbyInfra.length.toLocaleString() : 0}):
                    <input
                        type="checkbox"
                        checked={isLayerVisible}
                        onChange={toggleLayerVisibility}
                        style={{
                            marginLeft: '0.5rem',
                            cursor: 'pointer',
                            transform: 'scale(1.1)'
                        }}
                    />
                </div>
                
                {/* Type breakdown subtitle */}
                {nearbyInfra && nearbyInfra.length > 0 && (
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.2rem' }}>
                        {Object.entries(typeBreakdown).map(([type, count], idx) => (
                            <span key={type}>
                                {type.toUpperCase()}: {count}
                                {idx < Object.entries(typeBreakdown).length - 1 ? ' • ' : ''}
                            </span>
                        ))}
                    </div>
                )}

                {/* Filter dropdown */}
                {nearbyInfra && nearbyInfra.length > 0 && availableTypes.length > 1 && (
                    <div style={{ marginTop: '0.5rem' }}>
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            style={{
                                padding: '0.4rem 0.6rem',
                                fontSize: '0.8rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                color: '#333',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="all">All Types ({nearbyInfra.length.toLocaleString()})</option>
                            {availableTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.toUpperCase()} ({typeBreakdown[type]})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {(nearbyInfra && nearbyInfra.length > 0) ? (
                <div style={{ width: '100%', marginTop: '0.4rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                   <div style={{ maxHeight: '20vh', overflowY: 'auto', paddingBottom: '1rem' }}>
                     {filteredInfra
                       .sort((x, y) => calculateInfraDistance([latitude, longitude], [centroid(x).geometry.coordinates[1], centroid(x).geometry.coordinates[0]]) - calculateInfraDistance([latitude, longitude], [centroid(y).geometry.coordinates[1], centroid(y).geometry.coordinates[0]]))
                       .map((i, idx) => (
                       <div
                         key={idx}
                         style={{
                           display: 'flex',
                           flexDirection: 'column',
                           borderBottom: '1px solid rgba(88,88,88,0.8)',
                           padding: '0.8rem 0.4rem',
                           gap: '0.6rem',
                           marginBottom: idx === filteredInfra.length - 1 ? '1rem' : '0'
                         }}
                       >
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                           <div
                             style={{
                               color: 'black',
                               fontSize: '0.9rem',
                               fontWeight: 'bold'
                             }}
                           >
                             {i.properties.type.toUpperCase()}
                           </div>
                           <div
                             style={{
                               color: '#666',
                               fontSize: '0.9rem',
                               marginTop: '0.2rem'
                             }}
                           >
                             {calculateInfraDistance([latitude, longitude], [centroid(i).geometry.coordinates[1], centroid(i).geometry.coordinates[0]]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} miles away from fire
                           </div>
                         </div>
                         <div
                           onClick={() => { 
                            map.current.flyTo({ center: centroid(i).geometry.coordinates, zoom: 18, pitch: 0 });
                            showInfraPopup(centroid(i).geometry.coordinates, {
                               type: i.properties.type,
                               height: i.properties.height,
                               underground: i.properties.underground,
                               distance: calculateInfraDistance([latitude, longitude], [centroid(i).geometry.coordinates[1], centroid(i).geometry.coordinates[0]])
                            }) }}
                           style={{
                             width: '100%',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             gap: '0.6rem',
                             fontSize: '0.8rem',
                             color: 'white',
                             fontWeight: '600',
                             borderRadius: '8px',
                             padding: '0.4rem 0',
                             cursor: 'pointer',
                             background: 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)',
                             transition: 'all 0.2s ease'
                           }}
                           onMouseEnter={(e) => {
                             e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
                             e.target.style.transform = 'translateY(-1px)';
                             e.target.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.3)';
                           }}
                           onMouseLeave={(e) => {
                             e.target.style.background = 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)';
                             e.target.style.transform = 'translateY(0)';
                             e.target.style.boxShadow = 'none';
                           }}
                         >
                           View
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               </div>
             ) : (
               <div style={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 justifyContent: 'center', 
                 padding: '2rem',
                 textAlign: 'center',
                 color: '#666',
                 marginTop: '1rem'
               }}>
                 <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                   No infrastructure found
                 </div>
                 <div style={{ fontSize: '0.9rem' }}>
                   No infrastructure at risk found in this area
                 </div>
               </div>
             )}
        </div>
    )
}
